import React, { useEffect, useState } from 'react';
import { X, Loader2, Home, Upload, Trash2 } from 'lucide-react';
import { notifyError, notifySuccess } from '../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'des9fk8et';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'frg_profile_photos';

const inputCls =
  'w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25 transition';
const labelCls =
  'font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5 block';

const loadCloudinaryScript = () =>
  new Promise((resolve) => {
    if (window.cloudinary) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://upload-widget.cloudinary.com/latest/global/all.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

const LISTING_TYPES = [
  { value: 'rent', label: 'Rent - long-term' },
  { value: 'roommate', label: 'Roommate wanted' },
  { value: 'sublet', label: 'Sublet - short-term' },
];

/**
 * Member-facing housing listing submission form. Submissions land in
 * db.housing_listings with status="pending" and require admin approval.
 *
 * Set `adminMode=true` to call the /admin/housing/create endpoint instead,
 * which writes status="active" immediately.
 */
const SubmitHousingModal = ({ open, onClose, onSubmitted, adminMode = false }) => {
  const [form, setForm] = useState({
    listing_type: 'rent',
    city: '',
    title: '',
    description: '',
    price_monthly_usd: '',
    bedrooms: '1',
    bathrooms: '1',
    available_date: '',
    photos: [],
  });
  const [cities, setCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Fetch the canonical 13+ city list once when the modal opens.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/cities`);
        if (r.ok) {
          const data = await r.json();
          const items = Array.isArray(data) ? data : data.cities || [];
          setCities(items.map((c) => c.name).filter(Boolean).sort());
        }
      } catch {
        // Silent - the dropdown will be empty but the user can still close the modal.
      }
    })();
    loadCloudinaryScript();
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openUploadWidget = () => {
    if (!window.cloudinary || !CLOUD_NAME || !UPLOAD_PRESET) {
      notifyError('Photo upload is not configured.');
      return;
    }
    if (form.photos.length >= 5) {
      notifyError('You can upload up to 5 photos.');
      return;
    }
    setUploading(true);
    const remaining = 5 - form.photos.length;
    const w = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUD_NAME,
        uploadPreset: UPLOAD_PRESET,
        sources: ['local', 'url'],
        multiple: true,
        maxFiles: remaining,
        resourceType: 'image',
        folder: 'mundalo_housing_listings',
        maxFileSize: 8_000_000,
        cropping: false,
      },
      (err, result) => {
        if (err) {
          setUploading(false);
          notifyError('Photo upload failed.');
          return;
        }
        if (result && result.event === 'success') {
          setForm((f) => ({
            ...f,
            photos: [...f.photos, result.info.secure_url].slice(0, 5),
          }));
        }
        if (result && result.event === 'close') setUploading(false);
        if (result && result.event === 'queues-end') setUploading(false);
      },
    );
    w.open();
  };

  const removePhoto = (idx) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city) return notifyError('Pick a city.');
    if (!form.title.trim()) return notifyError('Add a listing title.');
    if (!form.description.trim() || form.description.trim().length < 10)
      return notifyError('Description must be at least 10 characters.');
    if (!form.available_date) return notifyError('Pick an available-from date.');

    setSubmitting(true);
    try {
      const endpoint = adminMode
        ? `${API}/api/admin/housing/create`
        : `${API}/api/housing/listings/submit`;
      const r = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_type: form.listing_type,
          city: form.city,
          title: form.title.trim(),
          description: form.description.trim(),
          price_monthly_usd: parseInt(form.price_monthly_usd || '0', 10) || 0,
          bedrooms: parseFloat(form.bedrooms || '0'),
          bathrooms: parseFloat(form.bathrooms || '0'),
          available_date: form.available_date,
          photos: form.photos,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        notifyError(d.detail || 'Could not submit your listing. Please try again.');
        setSubmitting(false);
        return;
      }
      notifySuccess(
        adminMode
          ? 'Listing published.'
          : 'Your listing has been submitted for review.',
      );
      if (onSubmitted) onSubmitted();
      onClose();
    } catch {
      notifyError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1F2E]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="submit-housing-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#F9F6EF] border border-[#EFE6CF] rounded-2xl max-w-2xl w-full my-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#EFE6CF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#F4ECD8] flex items-center justify-center">
              <Home className="w-5 h-5 text-[#1A5C6E]" />
            </div>
            <div>
              <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E]">
                {adminMode ? 'Admin · publish a listing' : 'Submit a listing'}
              </p>
              <h2 className="font-display italic text-2xl text-[#0A1F2E]">
                {adminMode ? 'New listing' : 'Share a place.'}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#0A1F2E]/55 hover:text-[#0A1F2E] transition"
            data-testid="submit-housing-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {!adminMode && (
            <p className="text-sm text-[#0A1F2E]/70 bg-[#F4ECD8] border border-[#EFE6CF] rounded-xl px-4 py-3">
              Listings are reviewed by the Mundalo team before going live. You'll
              see your listing on this page with a "Pending Review" badge until
              it's approved.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Listing type</label>
              <select
                value={form.listing_type}
                onChange={(e) => set('listing_type', e.target.value)}
                className={inputCls}
                data-testid="housing-form-type"
              >
                {LISTING_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>City</label>
              <select
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                className={inputCls}
                data-testid="housing-form-city"
              >
                <option value="">Pick a city…</option>
                {cities.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. Sunlit 2BR in Roma Norte, terrace included"
              className={inputCls}
              data-testid="housing-form-title"
              maxLength={160}
            />
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Neighborhood vibe, building amenities, transport, pets, who's a good fit. Be honest - it gets you the right inquiries."
              rows={4}
              className={inputCls}
              data-testid="housing-form-description"
              maxLength={4000}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>USD / month</label>
              <input
                type="number"
                min="0"
                value={form.price_monthly_usd}
                onChange={(e) => set('price_monthly_usd', e.target.value)}
                placeholder="1200"
                className={inputCls}
                data-testid="housing-form-price"
              />
            </div>
            <div>
              <label className={labelCls}>Bedrooms</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={form.bedrooms}
                onChange={(e) => set('bedrooms', e.target.value)}
                className={inputCls}
                data-testid="housing-form-bedrooms"
              />
            </div>
            <div>
              <label className={labelCls}>Bathrooms</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={form.bathrooms}
                onChange={(e) => set('bathrooms', e.target.value)}
                className={inputCls}
                data-testid="housing-form-bathrooms"
              />
            </div>
            <div>
              <label className={labelCls}>Available from</label>
              <input
                type="date"
                value={form.available_date}
                onChange={(e) => set('available_date', e.target.value)}
                className={inputCls}
                data-testid="housing-form-date"
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Photos · up to 5</label>
            <div className="grid grid-cols-5 gap-2 mb-2">
              {form.photos.map((url, i) => (
                <div
                  key={url}
                  className="relative aspect-square rounded-lg overflow-hidden border border-[#EFE6CF] group"
                >
                  <img src={url} alt={`Listing photo ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-[#0A1F2E]/70 hover:bg-[#B91C1C] text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    data-testid={`housing-form-remove-photo-${i}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {form.photos.length < 5 && (
                <button
                  type="button"
                  onClick={openUploadWidget}
                  disabled={uploading}
                  className="aspect-square rounded-lg border-2 border-dashed border-[#EFE6CF] hover:border-[#2CA8A0]/60 flex flex-col items-center justify-center text-[#0A1F2E]/55 text-[10px] gap-1 transition disabled:opacity-50"
                  data-testid="housing-form-upload-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-[#EFE6CF]">
            <button
              type="button"
              onClick={onClose}
              className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/60 hover:text-[#0A1F2E] px-4 py-2 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white rounded-full px-5 py-2.5 font-mono uppercase text-[11px] tracking-[0.15em] transition disabled:opacity-50"
              data-testid="housing-form-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {adminMode ? 'Publish listing' : 'Submit for review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitHousingModal;
